/**
 * The keyboard map (design 1642–1717).
 *
 * Data rather than markup, for two reasons. The popover is a *reference* — a
 * console this dense needs one, and it is the same list whatever seat is
 * looking at it — and the same table is what the key handler will consult once
 * the targets exist.
 *
 * **Rendering the map and binding a key are separate decisions.** The map states
 * what the console will do; the handler binds only what it can actually do
 * today, because a key bound to nothing is worse than a key not bound — it
 * teaches a player the console is unresponsive.
 *
 * Phase 3 closed most of the gap and Phase 5 closed the rest of it: `F`,
 * `DBL-CLICK`, `⌥ HOVER` and `DRAG` all act on the theatre map, and three of
 * those four are the vendored renderer's own gestures rather than key bindings —
 * they light up when the canvas mounts and appear in no handler here.
 * `BOUND_HOTKEYS` below is the honest list of what this console listens for.
 *
 * ## Three corrections the audit forced
 *
 * 1. **`↑ ↓ STEP THROUGH ROSTER` is gone.** It was advertised here from Phase 1
 *    and bound nowhere — there is no `ArrowUp`/`ArrowDown` handler anywhere in
 *    `console/`. This file's own rule is that a key bound to nothing is worse
 *    than a key not bound, because it teaches a player the console is
 *    unresponsive, and the row broke it for eight phases. It comes back when the
 *    roster owns a cursor to step, which is a rail's feature and not a label's.
 * 2. **`1–5 DOMAIN` was wrong twice.** There is no domain concept in this
 *    console, and the shell binds `1–9` to *tab selection*. The row named a
 *    feature that does not exist, on a key that does something else, and hid the
 *    console's most useful navigation shortcut. It now reads `1–9 SELECT TAB`
 *    and sits under ANYWHERE, where a global key belongs.
 * 3. **The modifiers are spelled, not glyphed.** `⌘`, `⌥` and `⇧` named keys
 *    that are not on most of these laptops, and `⌥` rendered as a broken glyph
 *    in the bundled font. The handlers have always accepted `metaKey || ctrlKey`
 *    and `altKey`, so only the card was Mac-only.
 *
 * `BOUND_HOTKEYS` did not catch any of this, and could not: it checks that every
 * key in the popover is one the console listens for. It says nothing about
 * whether the *action* beside the key is the one that happens.
 *
 * **Phase 8 adds two rows and binds both.** `←` and `→` walk the intel archive,
 * and they are the first keys since Phase 3 to be added to `BOUND_HOTKEYS`: the
 * intel tab has no vendored renderer, so unlike Phases 6 and 7 there is nothing
 * else that could have bound them.
 *
 * **Phase 6 adds four rows and no handler at all.** `V`, `R`, `G` and `[ ]` are
 * bound inside `vendor/maps/orbital-map.js` — on `document`, guarded on
 * `isConnected`, on `INPUT`/`TEXTAREA` and on modifiers — so they light up when
 * the SDA canvas mounts and go away with it. None of the five collides with the
 * shell's nine (`l p c f d a m t b`), which is why the vendored element could
 * keep them. They are listed in `CANVAS_HOTKEYS` rather than `BOUND_HOTKEYS`,
 * because a reviewer checking this file against the key handler would otherwise
 * find four keys that are not in it.
 */

export interface HotkeyRow {
  readonly keys: string;
  readonly action: string;
  /**
   * The tabs this row is true on. Absent means every tab.
   *
   * Three of the shared rows are gestures of `vendor/maps/region-map.js` alone,
   * and the flat map showed them on every board — `DRAG` needs a `bs-engage` that
   * only that renderer emits, `DBL-CLICK` needs a `dblclick` listener only it
   * binds, and `ALT HOVER` needs its `peekReach`, which is a range overlay the
   * orbital board has no concept of. This file's own rule is that a key bound to
   * nothing is worse than a key not bound; a *gesture* listed where it does not
   * fire is the same failure in the one surface an operator opens when stuck.
   */
  readonly tabs?: readonly string[];
}

export interface HotkeySection {
  readonly title: string;
  readonly rows: readonly HotkeyRow[];
}

export const HOTKEY_MAP: readonly HotkeySection[] = Object.freeze([
  Object.freeze({
    title: 'SELECT',
    rows: Object.freeze([
      { keys: '/', action: 'SEARCH ROSTER' },
      // `F` needs a mounted canvas to focus. Three tabs have one; the warning
      // board does not while `MwMap.MW_MAP_ENABLED` is false, and a gesture
      // listed where it does not fire is this file's own named failure. Add
      // `'MW'` here when the map comes back.
      { keys: 'F', action: 'FOCUS ON MAP', tabs: ['THEATRE', 'SDA', 'CYBER'] },
      { keys: 'P', action: 'PIN TO COMPARE' },
      { keys: 'C', action: 'COMPARE PINNED' },
      { keys: 'CTRL CLICK', action: 'ADD TO BATCH' },
      { keys: 'ESC', action: 'BACK OUT ONE LAYER' },
    ]),
  }),
  Object.freeze({
    title: 'ORDER',
    rows: Object.freeze([
      { keys: 'D', action: 'EXPAND / COLLAPSE BUILDER' },
      { keys: 'A M T', action: 'ARM ASSET / MISSION / TARGET' },
      { keys: 'TAB', action: 'NEXT SLOT (ARMED)' },
      { keys: 'SHIFT TAB', action: 'PREVIOUS SLOT (ARMED)' },
      { keys: '1–9', action: 'TAKE NTH ROW (ARMED)' },
      { keys: 'SHIFT CLICK', action: 'FILL NATURAL SLOT' },
      { keys: 'ALT CLICK', action: 'FILL TARGET SLOT' },
      { keys: 'DRAG', action: 'ASSET ONTO CONTACT = ORDER', tabs: ['THEATRE'] },
      { keys: 'B', action: 'APPLY DRAFT TO BATCH' },
      { keys: 'ENTER', action: 'QUEUE / CONFIRM' },
      { keys: 'CTRL Z', action: 'UNDO' },
    ]),
  }),
  Object.freeze({
    title: 'ANYWHERE',
    rows: Object.freeze([
      { keys: '1–9', action: 'SELECT TAB' },
      { keys: 'RIGHT-CLICK', action: 'ACTIONS FOR THAT OBJECT' },
      { keys: 'DBL-CLICK', action: 'FOCUS / ZOOM TO ZONE', tabs: ['THEATRE'] },
      { keys: 'ALT HOVER', action: 'PEEK REACH', tabs: ['THEATRE'] },
      // Not in the design's map, and added rather than left unstated: the theme
      // toggle is one of the three targets that exists today, so it is one of
      // the three keys actually bound. `L` is the only letter the design's map
      // leaves free across all three sections.
      { keys: 'L', action: 'LIGHT / DARK' },
      { keys: '?', action: 'THIS PANEL' },
    ]),
  }),
]);

/**
 * The SDA block (design 1703–1717), shown only on the board that answers it.
 *
 * The design gates it on `sdaView` and so does this console: the four keys are
 * the *element's*, and an element that is not mounted does not listen. A panel
 * that listed them on every tab would be four more keys bound to nothing.
 */
export const SDA_HOTKEYS: HotkeySection = Object.freeze({
  title: 'SDA',
  rows: Object.freeze([
    { keys: 'V', action: '2D / 3D' },
    { keys: 'R', action: 'RESET VIEW' },
    { keys: 'G', action: 'LEGEND' },
    { keys: '[ ]', action: 'REGIME' },
  ]),
});

/**
 * The intel reader's section (design script 2651–2652), shown on its own tab.
 *
 * Unlike the SDA block above, these two keys are **this console's** rather than a
 * vendored element's: there is no renderer on this tab to bind anything. So they
 * are in `BOUND_HOTKEYS`, the shell's own handler answers them, and they are
 * gated the same way every other key here is — the shell passes `stepReport` only
 * on the INTEL tab, so `←` and `→` are unbound everywhere else rather than bound
 * to nothing.
 */
export const INTEL_HOTKEYS: HotkeySection = Object.freeze({
  title: 'INTEL',
  rows: Object.freeze([
    { keys: '←', action: 'OLDER TURN' },
    { keys: '→', action: 'NEWER TURN' },
  ]),
});

/**
 * The warning board's section, and the last seat to get one.
 *
 * Missile Warning is the only board in the console with a clock running inside
 * the window, and until now it was the only one with no key at all: selecting a
 * track, opening the reference and toggling the dock were mouse-only, on the
 * seat least able to spare the hand. Worse, the two header controls sit under
 * the alert lane in the last minute of the window, so for that minute there was
 * no route to them at all.
 *
 * **The mnemonic keys were both taken, and guessing is worse than choosing.**
 * `T` arms the target slot and `R` is `orbital-map.js`'s reset — a key this
 * console is forbidden to bind, and the check that forbids it is in
 * `order-hotkeys.test.tsx`. So the panels take two free letters that appear in
 * their own labels, and the card is how anybody learns them. The arrows need no
 * mnemonic: stepping a list is what they do everywhere.
 */
export const MW_HOTKEYS: HotkeySection = Object.freeze({
  title: 'MW',
  rows: Object.freeze([
    { keys: '↑ ↓', action: 'STEP THROUGH TRACKS' },
    { keys: 'N', action: 'RECOGNITION TABLE' },
    { keys: 'W', action: 'TIMELINE' },
  ]),
});

/**
 * The cyber board's section — **and it is deliberately not here.**
 *
 * `vendor/maps/cyber-map.js` binds exactly one document key, `Escape`, and the
 * shell already lists that under GLOBAL. Its zoom, its fit and its legend are
 * buttons on the canvas, not keys. So Phase 7 adds **no** section rather than a
 * section of one row restating a key the popover already shows, and there is
 * nothing here for a CYBER seat that a THEATRE seat does not also get.
 *
 * The one thing worth knowing is written where somebody hits it: the `Escape`
 * overlap is Phase 6's finding 6, and it is the same on all three canvases.
 */

/** The map, for the tab in front of the operator. */
export function hotkeySections(tab: string | null): readonly HotkeySection[] {
  const base =
    tab === 'SDA'
      ? [...HOTKEY_MAP, SDA_HOTKEYS]
      : tab === 'INTEL'
        ? [...HOTKEY_MAP, INTEL_HOTKEYS]
        : tab === 'MW'
          ? [...HOTKEY_MAP, MW_HOTKEYS]
          : HOTKEY_MAP;

  // A row gated to other tabs is dropped, and a section emptied by that is
  // dropped with it — a heading over nothing is not a statement about anything.
  return base
    .map((section) => ({
      ...section,
      rows: section.rows.filter((row) => row.tabs === undefined || row.tabs.includes(tab ?? '')),
    }))
    .filter((section) => section.rows.length > 0);
}

/**
 * The keys the console actually binds today, in the order a reader meets them.
 *
 * Kept honest deliberately: this list is the one place a reviewer can check the
 * hotkey popover against the handler without reading either. Phase 2 bound `/`,
 * `p` and `c` and did not update it, which is exactly the drift the list exists
 * to make visible.
 *
 * `A M T`, `Tab`, `1–9` and `⏎` are further gated on the builder being usable at
 * all — a read-only seat and a closed window withdraw the strip, and its keys go
 * with it. `←` and `→` are gated on the INTEL tab being the one on screen.
 */
export const BOUND_HOTKEYS = Object.freeze([
  '?',
  'Escape',
  'l',
  '/',
  'p',
  'c',
  'f',
  'd',
  'a',
  'm',
  't',
  'b',
  'Tab',
  'Enter',
  '1-9',
  'Meta+z',
  // Phase 8's pair, and the first rows added to this list since Phase 3. They are
  // ours rather than a canvas's — `CANVAS_HOTKEYS` below is where a vendored
  // element's keys go, and there is no renderer on the intel tab at all.
  'ArrowLeft',
  'ArrowRight',
  // The warning board's three, gated on the MW tab the way the intel pair is
  // gated on its own. `n` and `w` rather than `r` and `t`: the mnemonic keys
  // were taken, and the check below is what stops `r` being taken twice.
  'ArrowUp',
  'ArrowDown',
  'n',
  'w',
] as const);

/**
 * Keys a **vendored canvas** binds, which this console does not.
 *
 * `orbital-map.js` listens on `document` for these five while it is connected.
 * They are here so the honesty check above stays a check: every key in the
 * hotkey popover is in one of these two lists, and neither list is written by
 * the thing it describes.
 *
 * `cyber-map.js` adds nothing to this list. It binds one key, `Escape`, which is
 * already in `BOUND_HOTKEYS` because the shell binds it too — the overlap Phase
 * 6 found on the other two canvases, and unchanged by a third.
 */
export const CANVAS_HOTKEYS = Object.freeze(['v', 'r', 'g', '[', ']'] as const);
